import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import AppConfig, { MAX_AUDIO_FILE_DURATION, STATIC_PATH } from "../../../constants/config";
import { font } from "../../../constants/font";
import useFileUpload from "../../../helper/hooks/useFileUpload";
import content from "../../../constants/content";
import { CloudWrapper, Flex, UploadContainer, UploadSection, MobileFlex } from "./upload-components";
import Action from "../../../common-components/Action";
import Typography from "../../../common-components/Typography";
import { resetUploadModal, setUploadModal } from "../../../redux/actions/appUtils";
import useNotify from "../../../helper/hooks/useNotify";
import vmTheme from "../../../constants/theme";
import { getAudioMetaData } from "../../../helper/getAudioMetaData";
import getAudioData from "../../../helper/getAudioData";
import useModal from "../../../helper/hooks/useModal";
import { getSupportedFilesContent, PLAN_CONFIG } from "../../../constants";
import ActionTypes from "../../../constants/action-types";
import useUpgrade from "../../../helper/hooks/useUpgrade";
import useMobileDetection from "../../../helper/hooks/useMobileDetection";

const DragUpload = () => {
    const editFileRef = useRef(null);
    const dispatch = useDispatch();
    const { success } = useNotify();
    const { prepareMedia, uploadResponse, isFailed } = useFileUpload();
    const theme = useSelector((state) => state.app.get("theme"));
    const [dragging, setDragging] = useState(false);
    const [isLoading, setLoading] = useState(false);
    const userDetails = useSelector((store) => store.userDetails);
    const { userId } = userDetails;
    const appReducer = useSelector((state) => state.app);
    const uploadData = appReducer.get("upload").toJS();
    const { plan } = useSelector((store) => store.userDetails);
    const { showModal, hideModal } = useModal();
    const { showUpgrade } = useUpgrade();
    const isMobile = useMobileDetection();

    useEffect(() => {
        if (uploadResponse?.res?.id && !uploadData.isCancel) {
            dispatch(setUploadModal({
                show: false,
                assetId: uploadResponse?.res?.id,
                assetData: uploadResponse.res,
            }));
            success(content.FILE_UPLOADED);
        }
        if (isFailed) {
            dispatch(resetUploadModal());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uploadResponse, isFailed]);

    const onAction = useCallback(() => {
        editFileRef.current.click();
        setLoading(true);
        setTimeout(() => {
            setLoading(false);
        }, 2000);
    }, [editFileRef]);

    const onClose = useCallback(() => {
        hideModal();
        dispatch({
            type: ActionTypes.SHOW_UPLOAD_MODAL,
            payload: {
                show: false,
                mode: null,
            },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onRestrict = useCallback((file) => {
        return getAudioData(file).then((duration) => {
            const fileSizeValid = file.size <= PLAN_CONFIG.SIZE_OPTIONS[plan];
            const files = Array.isArray(file) ? file : [file];
            const isAllFilesNotValid = files.every((item) => PLAN_CONFIG.SIZE_OPTIONS[plan] < item.size);
            const maxFileDuration = MAX_AUDIO_FILE_DURATION * 60;
            const durationExceed = duration > maxFileDuration;

            // If duration exceeds and file size is invalid
            if (durationExceed && isAllFilesNotValid) {
                showModal({
                    mainText: `File Too Large!`,
                    subText: `Please upload a audio under ${getSupportedFilesContent(plan)} and shorter than 5 minutes.`,
                    confirmText: null,
                    cancelText: `Upload Another File`,
                    upgradeDesign: true,
                    onSave: onClose,
                    cancelBackground: "#00baff",
                    hoverCancelBgColor: "#00baff",
                    fontsColor: "#fff",
                    cancelFontColor: "#fff",
                    isDisableDrag: true,
                });
                return false; // Invalid file
            }

            // Check if file size is valid
            if (!fileSizeValid) {
                showUpgrade({
                    headerText: 'Audio too large!',
                    subText: 'Upgrade your plan or upload a smaller file.',
                    type: 'uploadLimit',
                });
                hideModal();
                dispatch({
                    type: ActionTypes.SHOW_UPLOAD_MODAL,
                    payload: {
                        show: false,
                        mode: null,
                    },
                });
                return false;
            }

            if (durationExceed) {
                showModal({
                    mainText: `Audio too long!`,
                    subText: `Audio duration exceeds 5 minutes limit. Upload a shorter video.`,
                    confirmText: null,
                    cancelText: "Upload another file",
                    cancelBackground: "#00baff",
                    hoverCancelBgColor: "#00baff",
                    fontsColor: "#fff",
                    cancelFontColor: "#fff",
                    onSave: onClose,
                    isDisableDrag: true,
                });
                return false;
            }

            return true;
        }).catch(error => {
            console.error("Error validating file:", error);
            return false;
        });
    }, [plan, showModal, onClose, showUpgrade, hideModal, dispatch]);


    const processFile = useCallback((file) => {
        const blobSrc = URL.createObjectURL(file);
        getAudioMetaData(blobSrc);

        const payload = {
            active: 1,
            created: new Date().toISOString(),
            duration: file.duration || 0,
            id: file.id,
            name: file.name,
            parent: "0",
            product: "vmaker",
            roottype: "file",
            source_type: "mp3",
            src: file.src,
            tags: "userupload",
            type: "AUDIO",
            user_id: userId,
            isBlob: true,
        };

        dispatch(setUploadModal({
            show: false,
            assetId: uploadResponse?.res?.id,
            assetData: payload,
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uploadResponse, userId]);


    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (file) {
            const isFileValid = await onRestrict(file);

            if (!isFileValid) {
                e.target.value = null;
                return;
            }
            prepareMedia(file);
            processFile(file);
            e.target.value = null;
        }
    };


    const onDrop = useCallback(async (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragging(false);

        const file = e.dataTransfer.files[0];
        if (file) {
            const isFileValid = await onRestrict(file);
            if (!isFileValid) {
                return;
            }
            prepareMedia(file);
            processFile(file);
        }
    }, [onRestrict, prepareMedia, processFile]);


    const onDragOver = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragging(true);
    }, []);

    const onDragLeave = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragging(false);
    }, []);

    return (
        <UploadContainer
            onDrop={onDrop}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            dragging={dragging}
            isMobile={isMobile}
            onClick={isMobile ? null : onAction}
        >
            <input
                type="file"
                ref={editFileRef}
                style={{ display: "none" }}
                accept={AppConfig.UPLOADS.SUPPORTED_AUDIO_TYPES}
                onChange={handleFileUpload}
            />
            {!isMobile ? (
                <UploadSection>
                    <CloudWrapper>
                        <img
                            src={`${STATIC_PATH}vm-uploads.svg`}
                            alt="upload-icon"
                            style={{ cursor: "pointer" }}
                        />
                    </CloudWrapper>

                    <Flex>
                        <Typography
                            content={content.CHOOSE_A_FILE}
                            color={vmTheme[theme].secondaryBorderColor}
                            font={font.mediumMiniBase}
                            cursor="pointer"
                            padding="3px 0px"
                            textStyles={{ textDecoration: "underline" }}
                        />
                        <Typography
                            content={content.DRAG_AND_DROP}
                            color={vmTheme[theme].panelColor}
                            font={font.mediumMiniBase}
                            cursor="pointer"
                            padding="3px 0px"
                        />
                    </Flex>
                    <Typography
                        innerContent={`Max file: ${getSupportedFilesContent(
                            plan,
                            "audio"
                        )} | Max durations: 5 minutes | Supported format: .mp3 and .wav.`}
                        color={vmTheme[theme].sbSecondaryColor}
                        font={font.normal_14_20}
                        padding="2px 0px"
                        width={"auto"}
                        align={"center"}
                        cursor="pointer"
                        enableTrim={false}
                    />
                </UploadSection>
            ) : (
                <MobileFlex>
                    <Typography
                        innerContent={`Max file: ${getSupportedFilesContent(
                            plan,
                            "audio"
                        )} | Max durations: 5 minutes | Supported format: .mp3 and .wav.`}
                        color={vmTheme[theme].sbSecondaryColor}
                        font={font.normal_14_20}
                        padding="2px 0px"
                        width={"auto"}
                        align={"center"}
                        cursor="pointer"
                        enableTrim={false}
                    />
                    <Action
                        text="choose your audio"
                        onClick={onAction}
                        height="auto"
                        background="linear-gradient(180deg, #FFFFFF 0%, #FFFFFF 26%, #FAFAFA 100%)"
                        borderRadius="8px"
                        className="upload-btn-mob"
                        fontColor="#1F222E"
                        margin="22px 0px"
                        isLoading={isLoading}
                        font={font.semi_bold_21}
                        customBeforeIcon={`${STATIC_PATH}vm-uploads.svg`}
                    />
                </MobileFlex>
            )}
        </UploadContainer>

    );
};

export default DragUpload;
